import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import TemplateEditor from "@/components/TemplateEditor";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChevronLeft, Download, RefreshCw, AlertTriangle, FileText } from "lucide-react";
// Phase H.14a — inline PDF preview for uploaded templates.
import UploadedTemplatePreview from "@/components/UploadedTemplatePreview";
// Phase A1 — AI generator panel (invoice / contract / ACH; super_admin only).
import TemplateAIGeneratorPanel from "@/components/TemplateAIGeneratorPanel";
import { Sparkles } from "lucide-react";

import { getBackendBase } from "@/lib/api";
const BACKEND_URL = getBackendBase();  // Phase APEX-C — same-origin only. See lib/api.js.

export default function TemplateDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "super_admin";
    const [template, setTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editName, setEditName] = useState("");
    const [editCategory, setEditCategory] = useState("");
    const [editDescription, setEditDescription] = useState("");
    const [busyReplace, setBusyReplace] = useState(false);
    const fileInputRef = useRef(null);
    // Phase N — AI generator panel state (only matters for invoice templates).
    const [generatorOpen, setGeneratorOpen] = useState(false);
    const [generatorEnabled, setGeneratorEnabled] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/templates/${id}`);
            setTemplate(data);
            setEditName(data.name);
            setEditCategory(data.category || "");
            setEditDescription(data.description || "");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Phase N — fetch the generator enable flag once per page load so we can
    // gate the toolbar button + show the in-dialog disabled banner.
    useEffect(() => {
        if (!isSuperAdmin) return;
        (async () => {
            try {
                const { data } = await api.get("/admin/system-settings");
                setGeneratorEnabled(data?.template_generator_enabled !== false);
            } catch (_) { /* default to enabled — endpoint surfaces 503 if not */ }
        })();
    }, [isSuperAdmin]);

    // Phase N follow-up — imperative ref into TemplateEditor so the AI
    // generator can apply raw HTML byte-for-byte (bypassing TipTap's
    // schema-stripping setContent).
    const editorRef = useRef(null);

    // Phase N — apply generator output. Push the raw HTML into the
    // editor's HTML source tab via the imperative ref. The editor's
    // own debounced auto-save then PATCHes the body_html to the
    // backend within ~1.2s (raw-HTML path bypasses TipTap), so we
    // deliberately DO NOT issue a separate PATCH from here — that
    // would bump `updated_at` and remount the editor mid-apply,
    // wiping the html-tab state we just set.
    const applyGeneratedBody = (bodyHtml) => {
        if (!editorRef.current?.applyRawBodyHtml) {
            toast.error("Editor not ready — refresh and try again.");
            return;
        }
        editorRef.current.applyRawBodyHtml(bodyHtml);
    };

    // Phase H.13a — extract the editor-side `_trigger` flag (manual|auto) so
    // it lands on the PATCH endpoint as a query param rather than a body
    // field. The PATCH schema doesn't know about it; only the
    // version-history snapshot does.
    const _splitTrigger = (payload) => {
        const { _trigger, ...rest } = payload || {};
        const trigger = _trigger === "auto" ? "auto" : "manual";
        return { trigger, body: rest };
    };

    const onBuilderSave = async (payload) => {
        setSaving(true);
        const { trigger, body } = _splitTrigger(payload);
        try {
            const { data } = await api.patch(`/admin/templates/${id}?trigger=${trigger}`, body);
            setTemplate(data);
            toast.success("Template saved");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    // Phase H.11 — silent variant the editor uses for debounced auto-save.
    // No toast, no spinner on the manual button; errors propagate so the
    // editor's pill can render the retry state.
    const onBuilderAutoSave = async (payload) => {
        const { trigger, body } = _splitTrigger(payload);
        const { data } = await api.patch(`/admin/templates/${id}?trigger=${trigger}`, body);
        // Phase N follow-up — DO NOT bump `template.updated_at` on local
        // state for normal auto-saves. The `<TemplateEditor key={...}>`
        // prop watches updated_at and would force-remount, wiping the
        // editor's tab + raw-HTML-mode state (which is precisely the
        // state the AI-Apply flow just set up). The editor already has
        // the latest content locally, so we only need to merge the
        // backend's metadata that *doesn't* affect the remount key.
        setTemplate((prev) => prev
            ? { ...prev, ...data, updated_at: prev.updated_at, version_no: data.version_no }
            : data);
    };

    // Phase H.13a — reload the editor when a historical version is restored.
    // The backend already wrote the new "current" content + a fresh version
    // row; we just need to re-fetch so the TipTap editor remounts with the
    // restored body_html.
    const onVersionRestored = async () => {
        await load();
        toast.success("Editor reloaded with the restored content.");
    };

    const saveMetadata = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/admin/templates/${id}`, {
                name: editName,
                category: editCategory,
                description: editDescription,
            });
            setTemplate(data);
            toast.success("Metadata saved");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const replaceFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setBusyReplace(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`/admin/templates/${id}/replace-file`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setTemplate(data);
            toast.success("File replaced and tags re-parsed");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusyReplace(false);
        }
    };

    const downloadSource = async () => {
        try {
            // Phase H.14a — pass download=1 so the backend serves the file
            // with `Content-Disposition: attachment` (default is now
            // `inline` to support the new uploaded-template preview).
            const resp = await api.get(`/admin/templates/${id}/download-source?download=1`, { responseType: "blob" });
            const filename = template.original_filename || `${template.name}.html`;
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    if (loading) {
        return (
            <DashboardShell>
                <p className="text-sm text-slate-500" data-testid="template-detail-loading">Loading template…</p>
            </DashboardShell>
        );
    }
    if (!template) {
        return (
            <DashboardShell>
                <p className="text-sm text-red-600">Template not found.</p>
                <Button asChild variant="outline" className="mt-4">
                    <Link to="/admin/templates/contracts">Back to templates</Link>
                </Button>
            </DashboardShell>
        );
    }

    // Phase H.4 — route back to the correct typed list page.
    const listRoute = template.template_type === "ach_authorization"
        ? "/admin/templates/ach"
        : "/admin/templates/contracts";

    return (
        <DashboardShell>
            <div className="flex items-start justify-between flex-wrap gap-4 fade-up">
                <div>
                    <Link to={listRoute} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-3">
                        <ChevronLeft size={14} /> All templates
                    </Link>
                    <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900">
                        {template.source_type === "builder" ? "Builder template" : "Uploaded template"}
                    </p>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                        {template.name}
                    </h1>
                    {template.description && <p className="mt-2 text-sm text-slate-600">{template.description}</p>}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {/* Phase A1 — AI generator button. Available for
                        invoice / rental_agreement / ach_authorization
                        templates, super_admin only, and only when
                        source_type=builder (uploaded templates have no
                        editable body). The button label stays the same
                        across types — the panel itself adjusts its title
                        and copy based on the type it's opened for. */}
                    {isSuperAdmin
                        && ["invoice", "rental_agreement", "ach_authorization"].includes(template.template_type)
                        && template.source_type === "builder" && (
                        <Button
                            variant="outline"
                            onClick={() => setGeneratorOpen(true)}
                            data-testid="template-generate-ai-btn"
                            className="gap-1.5 border-blue-200 text-blue-900 hover:bg-blue-50"
                        >
                            <Sparkles size={14} /> Generate with AI
                        </Button>
                    )}
                    <Button variant="outline" onClick={downloadSource} data-testid="template-download-source" className="gap-1.5">
                        <Download size={14} /> Source
                    </Button>
                </div>
            </div>

            <Tabs defaultValue={template.source_type === "builder" ? "edit" : "details"} className="mt-6">
                <TabsList data-testid="template-tabs">
                    {template.source_type === "builder" && <TabsTrigger value="edit" data-testid="tab-edit">Edit</TabsTrigger>}
                    <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
                    <TabsTrigger value="tags" data-testid="tab-tags">Tags</TabsTrigger>
                </TabsList>

                {template.source_type === "builder" && (
                    <TabsContent value="edit" className="mt-4">
                        <TemplateEditor
                            ref={editorRef}
                            initialName={template.name}
                            initialCategory={template.category || ""}
                            initialDescription={template.description || ""}
                            initialBodyHtml={template.body_html || ""}
                            initialHeaderHtml={template.header_html || ""}
                            initialFooterHtml={template.footer_html || ""}
                            templateId={template.id}
                            canRestoreVersions={isSuperAdmin}
                            onSave={onBuilderSave}
                            onAutoSave={onBuilderAutoSave}
                            onVersionRestored={onVersionRestored}
                            saving={saving}
                            submitLabel="Save changes"
                            key={template.updated_at /* Phase H.13a — remount on restore to refresh TipTap */}
                        />
                    </TabsContent>
                )}

                <TabsContent value="details" className="mt-4">
                    {/* Phase H.14a — inline PDF preview above the metadata
                        card for uploaded templates only. Builder templates
                        already have the live TipTap editor on their Edit tab. */}
                    {template.source_type === "upload" && (
                        <UploadedTemplatePreview template={template} />
                    )}
                    <Card className="max-w-2xl">
                        <CardContent className="p-5 space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="d_name">Name</Label>
                                    <Input id="d_name" data-testid="detail-name-input" value={editName} onChange={(e) => setEditName(e.target.value)} />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="d_cat">Category</Label>
                                    <Input id="d_cat" data-testid="detail-category-input" value={editCategory} onChange={(e) => setEditCategory(e.target.value)} />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="d_desc">Description</Label>
                                <Input id="d_desc" data-testid="detail-description-input" value={editDescription} onChange={(e) => setEditDescription(e.target.value)} />
                            </div>
                            <div className="text-xs text-slate-500 space-y-1">
                                {template.original_filename && <div>Original file: <span className="font-mono">{template.original_filename}</span></div>}
                                <div>Source type: {template.source_type}</div>
                                <div>Last updated: {new Date(template.updated_at).toLocaleString()}</div>
                            </div>
                            <div className="flex gap-2 pt-2">
                                <Button onClick={saveMetadata} disabled={saving} data-testid="detail-save-button" className="bg-blue-900 hover:bg-blue-800">
                                    {saving ? "Saving…" : "Save metadata"}
                                </Button>
                                {template.source_type === "upload" && (
                                    <>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                            onChange={replaceFile}
                                            data-testid="detail-replace-file-input"
                                            className="hidden"
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={busyReplace}
                                            data-testid="detail-replace-file-button"
                                            className="gap-1.5"
                                        >
                                            <RefreshCw size={14} /> {busyReplace ? "Replacing…" : "Replace file"}
                                        </Button>
                                    </>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="tags" className="mt-4">
                    <Card className="max-w-2xl">
                        <CardContent className="p-5">
                            <h3 className="font-display font-semibold text-slate-900 mb-3 inline-flex items-center gap-2">
                                <FileText size={16} className="text-slate-500" /> Detected tags ({template.detected_tags.length})
                            </h3>
                            <div className="flex flex-wrap gap-1">
                                {template.detected_tags.length === 0 && <span className="text-slate-400 text-sm">No merge tags detected.</span>}
                                {template.detected_tags.map((t) => (
                                    <span key={t} data-testid={`detail-tag-${t}`} className="text-xs px-2 py-0.5 rounded-full bg-slate-50 text-slate-700 border border-slate-200 font-mono">{t}</span>
                                ))}
                            </div>
                            {template.unknown_tags.length > 0 && (
                                <>
                                    <h3 className="font-display font-semibold text-red-700 mt-5 mb-3 inline-flex items-center gap-2">
                                        <AlertTriangle size={16} /> Unknown tags ({template.unknown_tags.length})
                                    </h3>
                                    <p className="text-xs text-slate-500 mb-2">
                                        These tags don't match any field in the form. Fix typos or remove them.
                                    </p>
                                    <div className="flex flex-wrap gap-1">
                                        {template.unknown_tags.map((t) => (
                                            <span key={t} data-testid={`detail-unknown-${t}`} className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-100 font-mono">{t}</span>
                                        ))}
                                    </div>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Phase A1 — AI generator dialog. Mounts for invoice +
                rental_agreement + ach_authorization (the toolbar trigger
                is also gated). The panel adjusts its title / copy /
                API namespace based on the ``templateType`` prop. */}
            {isSuperAdmin
                && ["invoice", "rental_agreement", "ach_authorization"].includes(template.template_type) && (
                <TemplateAIGeneratorPanel
                    open={generatorOpen}
                    onOpenChange={setGeneratorOpen}
                    templateType={template.template_type}
                    initialName={template.name}
                    onApply={applyGeneratedBody}
                    generatorEnabled={generatorEnabled}
                />
            )}
        </DashboardShell>
    );
}
